import { API_BASE_URL, type ApiResponse } from '../config/api';

export const apiService = {
    // Estate Selector Welcome Flag
    async getEstateSelectorWelcomeFlag(): Promise<boolean> {
      try {
        const token = localStorage.getItem('account-token');
        const response = await fetch(`${API_BASE_URL}/api/user/tutorial-status/estate_selector_welcome`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { 'Authorization': `Bearer ${token}` } : {})
          },
          credentials: 'include',
        });
        if (!response.ok) return false;
        const data = await response.json();
        return !!(data.dismissed);
      } catch (error) {
        console.error('Failed to get estate selector welcome flag:', error);
        return false;
      }
    },

    async setEstateSelectorWelcomeFlag(): Promise<boolean> {
      try {
        const token = localStorage.getItem('account-token');
        const response = await fetch(`${API_BASE_URL}/api/user/tutorial-dismissed/estate_selector_welcome`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { 'Authorization': `Bearer ${token}` } : {})
          },
          credentials: 'include',
        });
        return response.ok;
      } catch (error) {
        console.error('Failed to set estate selector welcome flag:', error);
        return false;
      }
    },
  async login(email: string, password: string): Promise<ApiResponse> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/accounts/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
        credentials: 'include',
      });

      const responseData = await response.json();
      if (response.ok && responseData.token) {
        // Store token in localStorage
        localStorage.setItem('account-token', responseData.token);
      }
      
      // Return all response data including MFA and lockout fields
      return {
        success: responseData.success ?? response.ok,
        user: responseData.user,
        token: responseData.token,
        message: responseData.message,
        // MFA fields
        requiresMFA: responseData.requiresMFA,
        userId: responseData.userId,
        email: responseData.email,
        name: responseData.name,
        // Lockout fields
        lockedUntil: responseData.lockedUntil,
        remainingSeconds: responseData.remainingSeconds,
        // Verification field
        requiresVerification: responseData.requiresVerification,
      };
    } catch (error) {
      console.error('Login failed:', error);
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Failed to login',
      };
    }
  },

  async createAccount(data: { email: string; name: string; password: string }): Promise<ApiResponse> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/accounts/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
        credentials: 'include',
      });

      const responseData = await response.json();
      return {
        success: response.ok,
        user: responseData.user,
        token: responseData.token,
        message: responseData.message,
      };
    } catch (error) {
      console.error('Account creation failed:', error);
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Failed to create account',
      };
    }
  },
  // OAuth
  async getGoogleAuthUrl(userId?: string): Promise<{ url: string }> {
    try {
      const queryParams = userId ? `?userId=${userId}` : '';
      const response = await fetch(`${API_BASE_URL}/auth/google${queryParams}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });
      return await response.json();
    } catch (error) {
      console.error('Failed to get Google auth URL:', error);
      throw error;
    }
  },

  async getMicrosoftAuthUrl(userId?: string): Promise<{ url: string }> {
    try {
      const queryParams = userId ? `?userId=${userId}` : '';
      const response = await fetch(`${API_BASE_URL}/auth/microsoft${queryParams}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });
      return await response.json();
    } catch (error) {
      console.error('Failed to get Microsoft auth URL:', error);
      throw error;
    }
  },

  async handleGoogleCallback(_code: string): Promise<void> {
    // Callback is handled by backend redirect - this method is for compatibility
    console.log('Google callback handled by backend redirect');
  },

  async handleMicrosoftCallback(_code: string): Promise<void> {
    // Callback is handled by backend redirect - this method is for compatibility
    console.log('Microsoft callback handled by backend redirect');
  },

  async getOAuthTokens(provider: string, sessionId: string): Promise<{ access_token: string; refresh_token?: string; expires_at?: string }> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/tokens/${provider}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionId}`
        },
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error(`Failed to get OAuth tokens: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Failed to get OAuth tokens:', error);
      throw error;
    }
  },
  // Contact Sync
  async syncGoogleContacts(accessToken: string): Promise<ApiResponse> {
    try {
      // Use the request() method which automatically adds account-token for user auth
      // Send Google OAuth access token in body
      const response = await this.request('/api/contacts/sync/google', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ accessToken })
      });

      return {
        success: response.success,
        contacts: response.contacts,
        contactCount: response.contactCount,
        message: response.message
      };
    } catch (error) {
      console.error('Failed to sync Google contacts:', error);
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Failed to sync Google contacts'
      };
    }
  },

  async syncOutlookContacts(token: string): Promise<ApiResponse> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/contacts/sync/outlook`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include'
      });

      const data = await response.json();
      return {
        success: response.ok,
        contacts: data.contacts,
        message: data.message
      };
    } catch (error) {
      console.error('Failed to sync Outlook contacts:', error);
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Failed to sync Outlook contacts'
      };
    }
  },

  async getContactSyncStatus(): Promise<{
    google: { lastSync: string | null; contactCount: number };
    outlook: { lastSync: string | null; contactCount: number };
  }> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/contacts/sync/status`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Failed to get contact sync status:', error);
      return {
        google: { lastSync: null, contactCount: 0 },
        outlook: { lastSync: null, contactCount: 0 }
      };
    }
  },
  // KB Articles
  async getKBArticlesByFrame(frameId: string): Promise<ApiResponse> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/kb/frames/${frameId}/articles`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      const data = await response.json();
      return {
        success: response.ok,
        articles: data.articles,
        message: data.message,
      };
    } catch (error) {
      console.error('Failed to fetch KB articles:', error);
      return {
        success: false,
        articles: [],
        message: error instanceof Error ? error.message : 'Failed to fetch KB articles',
      };
    }
  },

  async getKBArticle(slug: string): Promise<ApiResponse> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/kb/articles/${slug}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      const data = await response.json();
      return {
        success: response.ok,
        article: data.article,
        message: data.message,
      };
    } catch (error) {
      console.error('Failed to fetch KB article:', error);
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Failed to fetch KB article',
      };
    }
  },

  async searchKBArticles(query: string, filters?: Record<string, unknown>): Promise<ApiResponse> {
    try {
      const params = new URLSearchParams();
      if (query) params.set('q', query);
      if (filters) {
        Object.entries(filters).forEach(([key, value]) => {
          if (Array.isArray(value)) {
            value.forEach(v => params.append(`${key}[]`, v));
          } else if (value != null) {
            params.set(key, value.toString());
          }
        });
      }

      const response = await fetch(`${API_BASE_URL}/api/kb/articles/search?${params.toString()}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      const data = await response.json();
      return {
        success: response.ok,
        articles: data.articles,
        message: data.message,
      };
    } catch (error) {
      console.error('Failed to search KB articles:', error);
      return {
        success: false,
        articles: [],
        message: error instanceof Error ? error.message : 'Failed to search KB articles',
      };
    }
  },
  // ... existing methods ...

  async requestPasswordReset(email: string): Promise<ApiResponse> {
    try {
      console.log('Requesting password reset from:', `${API_BASE_URL}/api/accounts/request-reset`);
      const controller = new AbortController();

      const response = await fetch(`${API_BASE_URL}/api/accounts/request-reset`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
        signal: controller.signal,
        credentials: 'include',
      });

      const data = await response.json();
      return {
        success: response.ok,
        message: data.message,
      };
    } catch (error) {
      console.error('Password reset request failed:', error);
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Failed to request password reset',
      };
    }
  },

  async resetPassword(token: string, newPassword: string): Promise<ApiResponse> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/accounts/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token, newPassword }),
      });

      const data = await response.json();
      return {
        success: response.ok,
        message: data.message,
      };
    } catch (error) {
      console.error('Password reset failed:', error);
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Failed to reset password',
      };
    }
  },

  // Executor notification
  async queueExecutorNotifications(executorData: { email: string; name: string; estateOwnerName: string }[]): Promise<ApiResponse> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/executors/notify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ executors: executorData }),
        credentials: 'include',
      });

      const data = await response.json();
      return {
        success: response.ok,
        message: data.message,
      };
    } catch (error) {
      console.error('Failed to queue executor notifications:', error);
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Failed to queue executor notifications',
      };
    }
  },

  // Memory sharing
  async sendMemoriesEmail(memoriesData: { recipientEmail: string; recipientName: string; senderName: string; memoriesLink?: string }): Promise<ApiResponse> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/memories/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(memoriesData),
        credentials: 'include',
      });

      const data = await response.json();
      return {
        success: response.ok,
        message: data.message,
      };
    } catch (error) {
      console.error('Failed to send memories email:', error);
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Failed to send memories email',
      };
    }
  },

  // Support request
  async createSupportRequest(supportData: { issueType: string; description: string; priority?: string }): Promise<ApiResponse> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/support/request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(supportData),
        credentials: 'include',
      });

      const data = await response.json();
      return {
        success: response.ok,
        message: data.message,
        ticketNumber: data.ticketNumber,
      };
    } catch (error) {
      console.error('Failed to create support request:', error);
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Failed to create support request',
      };
    }
  },

  // Cross-device data synchronization
  async reconcileUserData(localSession: unknown): Promise<unknown> {
    try {
      const token = localStorage.getItem('account-token');
      const headers: HeadersInit = {
        'Content-Type': 'application/json',
      };

      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${API_BASE_URL}/api/user/reconcile`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ localSession }),
        credentials: 'include',
      });

      const data = await response.json();
      if (response.ok) {
        return data.reconciledSession || localSession;
      } else {
        console.warn('Failed to reconcile user data:', data.message);
        return localSession;
      }
    } catch (error) {
      console.error('Failed to reconcile user data:', error);
      return localSession;
    }
  },

  // Verify data synchronization status
  async verifyDataSync(session: unknown): Promise<{
    synced: boolean;
    localSections: string[];
    backendSections: string[];
    missingSections: string[];
    outdatedSections: string[];
  }> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/user/sync-status`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ sessionId: session.id }),
        credentials: 'include',
      });

      const data = await response.json();
      if (response.ok) {
        return data.syncStatus;
      } else {
        return {
          synced: false,
          localSections: Object.keys(session.taskProgress || {}),
          backendSections: [],
          missingSections: [],
          outdatedSections: []
        };
      }
    } catch (error) {
      console.error('Failed to verify data sync:', error);
      return {
        synced: false,
        localSections: Object.keys(session.taskProgress || {}),
        backendSections: [],
        missingSections: [],
        outdatedSections: []
      };
    }
  },

  // Save frame data to backend for secure storage
  async saveFrameData(frameId: string, data: unknown): Promise<ApiResponse> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/frames/${frameId}/save`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
        credentials: 'include',
      });

      const responseData = await response.json();
      return {
        success: response.ok,
        message: responseData.message,
      };
    } catch (error) {
      console.error('Failed to save frame data:', error);
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Failed to save frame data',
      };
    }
  },

  // Generic request method for custom API calls
  async request(url: string, options: RequestInit = {}): Promise<unknown> {
    try {
      const token = localStorage.getItem('account-token');
      const headers: HeadersInit = {
        'Content-Type': 'application/json',
        ...options.headers,
      };

      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${API_BASE_URL}${url}`, {
        ...options,
        headers,
        credentials: 'include',
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      return data;
    } catch (error) {
      console.error(`[apiService] Request failed for ${url}:`, error);
      throw error;
    }
  },

  // ... rest of existing code ...
};